import { DestroyRef, effect, inject, Injector } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { debounceTime } from 'rxjs/operators';

import { SelectController } from '../../services/select-controller.service';
import { OpenStrategy } from '../../strategies/open/types';

export class OverlayOpenStrategy extends OpenStrategy {
  private selectController = inject(SelectController);
  private destroyRef = inject(DestroyRef);
  private injector = inject(Injector);

  public override init() {
    this.dropdownConnectedOverlay.hasBackdrop = false;
    this.dropdownConnectedOverlay.autoCloseOnUnpreventedClick = false;
    this.dropdownConnectedOverlay.width = this.options.overlayWidth!;
    if (this.options.overlayPosition) {
      this.dropdownConnectedOverlay.position = this.options.overlayPosition;
    }
    this.triggerOverlay.hasBackdrop = true;
    this.triggerOverlay.autoCloseOnUnpreventedClick = false;
    this.triggerOverlay.positions = [
      { originX: 'start', originY: 'top', overlayX: 'start', overlayY: 'top' },
    ];

    this.overlayTriggerOrigin$
      .pipe(debounceTime(0), takeUntilDestroyed(this.destroyRef))
      .subscribe(origin => {
        if (!origin) {
          this.dropdownConnectedOverlay.open = false;
          return;
        }
        if (this.dropdownConnectedOverlay.open) return;

        this.dropdownConnectedOverlay.origin = origin;
        const minWidth = this.options.overlayMinWidth ?? this.getInlineTriggerWidth();
        const width = this.options.overlayWidth ?? this.getInlineTriggerWidth();
        this.dropdownConnectedOverlay.width = width;
        this.dropdownConnectedOverlay.minWidth = minWidth;
        this.updateOverlaySize({ width, minWidth });
        this.dropdownConnectedOverlay.recreateOverlay();
        this.dropdownConnectedOverlay.positionStrategy.withGrowAfterOpen(true);
        this.dropdownConnectedOverlay.open = true;
      });

    effect(
      () => {
        this.triggerOverlay.width = this.getInlineTriggerWidth();
        this.triggerOverlay.origin = this.inlineTriggerOrigin;
        this.triggerOverlay.open = this.selectController.isOpen();
      },
      { injector: this.injector },
    );

    this.triggerOverlay.backdropClick.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.triggerOverlay.open = false;
      this.selectController.toggle(false);
    });

    this.selectController.select$
      .pipe(debounceTime(0), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.dropdownConnectedOverlay.updatePositionWithoutRedrawing();
      });
  }

  private getInlineTriggerWidth() {
    const htmlOrigin: HTMLElement = this.inlineTriggerOrigin.elementRef.nativeElement;
    return htmlOrigin.getBoundingClientRect().width;
  }
}
